// React Query hook for fetching metric definitions from the database
import { useQuery } from "@tanstack/react-query";
import { fetchMetricDefinitions, type MetricDefinition, type MetricCategory, type MetricType } from "./metricDefinitions";

/**
 * Hook to fetch all metric definitions from database (SINGLE SOURCE OF TRUTH)
 * Results are cached by React Query and the fetch function itself
 */
export function useMetricDefinitions() {
  return useQuery({
    queryKey: ["metric-definitions"],
    queryFn: async () => {
      const result = await fetchMetricDefinitions();
      // DEBUG: Check what we're actually getting
      const sampleKey = Object.keys(result)[0];
      const sampleDef = result[sampleKey];
      console.error("[useMetricDefinitions] Fetched data check:", {
        keysCount: Object.keys(result).length,
        sampleKey,
        sampleDef,
        sampleDefKeys: sampleDef ? Object.keys(sampleDef) : [],
        hasAllProps: sampleDef ? ('name' in sampleDef && 'goalKey' in sampleDef && 'unit' in sampleDef) : false
      });
      return result;
    },
    staleTime: Infinity, // NEVER mark stale
    gcTime: Infinity, // NEVER garbage collect  
  });
}

/**
 * Hook to get a specific metric by key
 */
export function useMetric(key: string) {
  const { data: metrics, ...rest } = useMetricDefinitions();
  return {
    ...rest,
    data: metrics?.[key],
  };
}

/**
 * Hook to get metrics filtered by category
 */
export function useMetricsByCategory(category: MetricCategory) {
  const { data: metrics, ...rest } = useMetricDefinitions();
  return {
    ...rest,
    data: metrics ? Object.values(metrics).filter(m => m.category === category) : [],
  };
}

/**
 * Hook to get metrics filtered by type
 */
export function useMetricsByType(type: MetricType) {
  const { data: metrics, ...rest } = useMetricDefinitions();
  return {
    ...rest,
    data: metrics ? Object.values(metrics).filter(m => m.type === type) : [],
  };
}

/**
 * Hook to get only HealthKit metrics
 */
export function useHealthKitMetrics() {
  const { data: metrics, ...rest } = useMetricDefinitions();
  return {
    ...rest,
    data: metrics ? Object.values(metrics).filter(m => m.isHealthKit) : [],
  };
}

/**
 * Hook to get only numeric metrics with goals
 */
export function useNumericMetricsWithGoals() {
  const { data: metrics, ...rest } = useMetricDefinitions();
  return {
    ...rest,
    data: metrics ? Object.values(metrics).filter(m => m.type === 'numeric' && m.goalKey) : [],
  };
}

/**
 * Hook to get default goals map (keyed by goalKey like 'calories_goal')
 */
export function useDefaultGoals() {
  const { data: metrics, ...rest } = useMetricDefinitions();
  
  const defaultGoals: Record<string, number> = {};
  if (metrics) {
    for (const metric of Object.values(metrics)) {
      if (metric.goalKey && metric.defaultGoal !== undefined) {
        defaultGoals[metric.goalKey] = metric.defaultGoal;
      }
    }
  }
  
  return {
    ...rest,
    data: defaultGoals,
  };
}
